import { useState, useEffect, useCallback, type CSSProperties, type KeyboardEvent } from "react";
import { formatMontant, parseMontant } from "@/lib/formatMontant";
import { cn } from "@/lib/utils";
import { inputFieldClass } from "@/lib/ui/formFieldClasses";

export interface InputMontantProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Suffixe à droite (ex. « FCFA »). Chaîne vide = pas de suffixe. */
  suffix?: string;
  /** Styles appliqués à l’input */
  style?: CSSProperties;
  /** Si true, affiche vide lorsque la valeur est 0 (champs optionnels type débit/crédit). */
  emptyWhenZero?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  /** Attribut data pour navigation au clavier (grilles) */
  "data-sr"?: string;
}

export function InputMontant({
  value,
  onChange,
  placeholder = "0",
  disabled = false,
  className = "",
  suffix = "FCFA",
  style,
  emptyWhenZero = false,
  onKeyDown: onKeyDownProp,
  onBlur: onBlurProp,
  "data-sr": dataSr,
}: InputMontantProps) {
  const displayFor = useCallback(
    (v: number) => (emptyWhenZero && v === 0 ? "" : formatMontant(v)),
    [emptyWhenZero]
  );

  const [displayValue, setDisplayValue] = useState(() => displayFor(value));

  useEffect(() => {
    setDisplayValue(displayFor(value));
  }, [value, displayFor]);

  const commit = useCallback(() => {
    const parsed = parseMontant(displayValue);
    onChange(parsed);
    setDisplayValue(emptyWhenZero && parsed === 0 ? "" : formatMontant(parsed));
  }, [displayValue, onChange, emptyWhenZero]);

  const handleBlur = () => {
    commit();
    onBlurProp?.();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === ".") {
      e.preventDefault();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
    onKeyDownProp?.(e);
  };

  const padRight = suffix ? "pr-14" : "pr-3";

  return (
    <div className="relative w-full min-w-0">
      <input
        data-sr={dataSr}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={(e) => setDisplayValue(e.target.value)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
        className={cn(inputFieldClass, padRight, "text-right", className)}
      />
      {suffix ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}
